/**
 * Map between raw decoded video pixels and the coach's displayed orientation
 * (CSS rotate/flip on the <video>, including MP4 metadata fix).
 *
 * Contract:
 * - Pointer / ROI / table corners → element (storage) space via SVG CTM
 * - Pose joints from the worker → display space (frames were transformed)
 * - Draw joints without re-applying CSS orientation (parent zoom/pan only)
 */

import {
  composeVideoCssTransform,
  cssTransformForUserOrientation,
  normalizeRotationDeg,
} from "./videoDisplayTransform";

/** Parse a CSS transform string into a DOMMatrix (rotate/scale only). */
export function cssTransformToMatrix(cssTransform, width = 1, height = 1) {
  if (!cssTransform?.trim()) return new DOMMatrix();
  const cx = width / 2;
  const cy = height / 2;
  const inner = parseSvgTransformList(cssTransform.trim());
  const m = new DOMMatrix();
  m.translateSelf(cx, cy);
  m.multiplySelf(inner);
  m.translateSelf(-cx, -cy);
  return m;
}

function parseSvgTransformList(cssTransform) {
  if (typeof document === "undefined") return new DOMMatrix();
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
  g.setAttribute("transform", cssTransform);
  svg.appendChild(g);
  const consolidated = g.transform?.baseVal?.consolidate?.();
  if (!consolidated?.matrix) return new DOMMatrix();
  const sm = consolidated.matrix;
  return new DOMMatrix([sm.a, sm.b, sm.c, sm.d, sm.e, sm.f]);
}

/** Parse rotate/scaleX/scaleY from a CSS transform into orientation fields. */
export function orientationFromCssTransform(cssTransform) {
  const s = String(cssTransform || "");
  let rotationDeg = 0;
  const rot = s.match(/rotate\(\s*(-?\d+(?:\.\d+)?)\s*deg\s*\)/i);
  if (rot) rotationDeg = normalizeRotationDeg(Number(rot[1]));
  const flipH = (s.match(/scaleX\(\s*-1\s*\)/gi) || []).length % 2 === 1;
  const flipV = (s.match(/scaleY\(\s*-1\s*\)/gi) || []).length % 2 === 1;
  return {
    rotation_deg: rotationDeg,
    flip_h: flipH,
    flip_v: flipV,
  };
}

export function buildDisplayViewSetup({
  rotationDeg = 0,
  flipH = false,
  flipV = false,
  metadataTransform = "",
} = {}) {
  return {
    metadata: orientationFromCssTransform(metadataTransform),
    user: {
      rotation_deg: normalizeRotationDeg(rotationDeg),
      flip_h: Boolean(flipH),
      flip_v: Boolean(flipV),
    },
  };
}

function orientationSteps(viewSetup) {
  if (!viewSetup) return [];
  if (viewSetup.metadata || viewSetup.user) {
    return [viewSetup.metadata, viewSetup.user].filter(
      (s) =>
        s &&
        (normalizeRotationDeg(s.rotation_deg || s.rotationDeg) ||
          s.flip_h ||
          s.flipH ||
          s.flip_v ||
          s.flipV)
    );
  }
  if (
    normalizeRotationDeg(viewSetup.rotation_deg || viewSetup.rotationDeg) ||
    viewSetup.flip_h ||
    viewSetup.flipH ||
    viewSetup.flip_v ||
    viewSetup.flipV
  ) {
    return [viewSetup];
  }
  return [];
}

/** One orientation step: flips first, then rotate (matches backend / CSS). */
function mapPointForwardStep(x, y, step) {
  let nx = x;
  let ny = y;
  if (step.flip_h || step.flipH) nx = 1 - nx;
  if (step.flip_v || step.flipV) ny = 1 - ny;
  const rot = normalizeRotationDeg(step.rotation_deg ?? step.rotationDeg ?? 0);
  if (rot === 90) {
    const t = nx;
    nx = ny;
    ny = 1 - t;
  } else if (rot === 180) {
    nx = 1 - nx;
    ny = 1 - ny;
  } else if (rot === 270) {
    const t = nx;
    nx = 1 - ny;
    ny = t;
  }
  return { x: nx, y: ny };
}

/** Element/storage norm → display/analysis norm. */
export function mapStorageToDisplayNorm(x, y, viewSetup) {
  let p = { x, y };
  for (const step of orientationSteps(viewSetup)) {
    p = mapPointForwardStep(p.x, p.y, step);
  }
  return p;
}

export function mapPointsStorageToDisplay(points, viewSetup) {
  if (!Array.isArray(points)) return points;
  return points.map((p) => {
    if (!p) return p;
    const m = mapStorageToDisplayNorm(p.x, p.y, viewSetup);
    return { ...p, x: m.x, y: m.y };
  });
}

export function buildDisplayTransformMatrix(
  { rotationDeg = 0, flipH = false, flipV = false, metadataTransform = "" } = {},
  width = 1,
  height = 1
) {
  const css = composeVideoCssTransform(metadataTransform, {
    rotationDeg,
    flipH,
    flipV,
  });
  return cssTransformToMatrix(css, width, height);
}

/** True only for an odd number of scaleX(-1). Rotate(180) is NOT a mirror for ROI ordering —
 * CTM already maps screen clicks into storage space; treating 180 as mirrored double-swaps A/B.
 */
export function isViewMirroredX(cssTransform) {
  const matches = String(cssTransform || "").match(/scaleX\(\s*-1\s*\)/gi);
  return Boolean(matches && matches.length % 2 === 1);
}

export function buildUserCssTransform(viewSetup) {
  if (!viewSetup) return "";
  const user = viewSetup.user || viewSetup;
  return cssTransformForUserOrientation({
    rotationDeg: user.rotation_deg ?? user.rotationDeg ?? 0,
    flipH: Boolean(user.flip_h ?? user.flipH),
    flipV: Boolean(user.flip_v ?? user.flipV),
  });
}

/**
 * Draw the video frame with the same center-origin CSS transform as the player.
 */
export function drawVideoWithDisplayTransform(ctx, videoEl, cssTransform) {
  const w = videoEl.videoWidth | 0;
  const h = videoEl.videoHeight | 0;
  if (!cssTransform?.trim()) {
    ctx.drawImage(videoEl, 0, 0, w, h);
    return;
  }
  ctx.save();
  ctx.translate(w / 2, h / 2);
  const m = cssTransformToMatrix(cssTransform, w, h);
  ctx.transform(m.a, m.b, m.c, m.d, m.e, m.f);
  ctx.drawImage(videoEl, -w / 2, -h / 2, w, h);
  ctx.restore();
}
